/**
 * Field Help — Time Entries
 *
 * Keys MUST match the time-entry field ids in
 * `features/time-entries/config/field-configs.ts` (TIME_ENTRY_FIELD_CONFIGS,
 * assembled from the `config/fields/time-entry-fields-*.ts` modules) so the "?"
 * popover attaches to the right column / editor row / card field.
 *
 * Relationship context (used in `relatedTo`): a time entry belongs to a ticket.
 * Company and contact are NOT linked directly — they derive THROUGH the ticket
 * (time_entry → ticket → contact → company). See
 * `lib/actions/relationships/AGENTS.md`.
 *
 * Billing note: `hours` and `billable_rate` are BigFloat (stored/serialized as
 * strings). Logged hours × rate is what the client gets billed; billable vs
 * non-billable drives revenue and utilization reporting.
 */

import type { EntityFieldHelp } from "./types";

export const timeEntryFieldHelp: EntityFieldHelp = {
  date_worked: {
    title: "Date Worked",
    description: "The day the work was actually performed.",
    usage: "Log against the real work date, not the date you're entering it — billing periods and utilization reports bucket by this field.",
  },
  ticket_id: {
    title: "Ticket",
    description: "The ticket this work was performed against. The primary link for every time entry.",
    usage: "Set this first — it's how the entry connects to the client and the work. Company and contact are derived from the ticket, never set directly on the entry.",
    relatedTo: "time entry → ticket → contact → company. Pick the ticket; the rest follows automatically.",
  },
  company_id: {
    title: "Company",
    description: "The client company being billed, auto-derived from the ticket.",
    usage: "Read-only — change the ticket to change this. Drives which company the logged hours bill against.",
    relatedTo: "Derived through the ticket (time entry → ticket → company), not linked directly.",
  },
  description: {
    title: "Description",
    description: "What work was performed during this entry.",
    usage: "This is what the client sees on the invoice line. Write it clearly enough to justify the hours — vague descriptions slow approvals and invite billing disputes.",
  },
  hours: {
    title: "Hours",
    description: "Logged work hours for this entry (decimal, e.g. 2.5).",
    usage: "Hours × Billable Rate is what the client is billed for billable entries. Keep it accurate — it's the basis of revenue, payroll, and utilization. Stored as a precise decimal string.",
    relatedTo: "Combined with Billable Rate to compute the billed amount.",
  },
  entry_type: {
    title: "Entry Type",
    description: "Whether the work is billable or non-billable.",
    usage: "Billable entries bill the client and count toward revenue; non-billable still count toward utilization but generate no revenue. Set it honestly — misclassifying inflates either the invoice or the utilization picture.",
    relatedTo: "Determines whether Hours × Billable Rate flows into client billing.",
  },
  billable_rate: {
    title: "Billable Rate",
    description: "Dollar-per-hour rate applied to this entry (decimal).",
    usage: "Auto-populated from the company's hourly rate when the entry is created; empty for monthly-contract clients (a flat fee applies instead). Hours × this rate is the billed amount. Stored as a precise decimal string.",
    relatedTo: "Combined with Hours to produce the client's charge. Sourced from the company's hourly rate.",
  },
  is_approved: {
    title: "Approved",
    description: "Whether the entry has been approved for billing.",
    usage: "Only approved entries should flow to invoicing. Read-only here — approval is set through the approval action, which also stamps Approved By and Approved At.",
    relatedTo: "Paired with Approved By and Approved At.",
  },
  sync_status: {
    title: "Sync Status",
    description: "Whether the entry has been pushed to QuickBooks.",
    usage: "Tracks the QuickBooks billing handoff. Read-only — driven by the sync process, not edited by hand.",
    relatedTo: "Set alongside QuickBooks ID and Synced At when the entry syncs.",
  },
  quickbooks_id: {
    title: "QuickBooks ID",
    description: "Reference id of the matching record in QuickBooks.",
    usage: "Read-only. Present once the entry has synced — use it to trace a billed line back to QuickBooks.",
    relatedTo: "Populated when Sync Status reaches synced.",
  },
  approved_at: {
    title: "Approved At",
    description: "Timestamp when the entry was approved.",
    usage: "Read-only — stamped by the approval action. Anchors billing-readiness reporting.",
    relatedTo: "Set together with Approved and Approved By.",
  },
  synced_at: {
    title: "Synced At",
    description: "Timestamp when the entry was synced to QuickBooks.",
    usage: "Read-only — set by the sync process. A long gap between Approved At and Synced At flags a billing backlog.",
    relatedTo: "Set together with Sync Status and QuickBooks ID.",
  },
  start_time: {
    title: "Start Time",
    description: "Optional clock time the work started.",
    usage: "Use when you track exact work windows rather than just total hours. Hours is the field that drives billing — start/end times are supporting detail.",
    relatedTo: "Pairs with End Time; Hours remains the billed quantity.",
  },
  end_time: {
    title: "End Time",
    description: "Optional clock time the work ended.",
    usage: "Optional companion to Start Time for precise work windows. Hours, not this span, is what bills.",
    relatedTo: "Pairs with Start Time.",
  },
  user_id: {
    title: "User",
    description: "The engineer who performed and logged the work.",
    usage: "Auto-filled from the logged-in user; read-only. Drives whose utilization and billable hours this entry counts toward.",
    relatedTo: "Connects the entry to the engineer for utilization and payroll reporting.",
  },
  approved_by: {
    title: "Approved By",
    description: "The user who approved the entry for billing.",
    usage: "Read-only — set by the approval action. Provides the audit trail for who cleared the entry to bill.",
    relatedTo: "Set together with Approved and Approved At.",
  },
  created_at: {
    title: "Created",
    description: "When the time entry record was created.",
    usage: "Read-only. Useful for catching entries logged long after the work date — a sign of delayed time tracking.",
  },
  updated_at: {
    title: "Last Updated",
    description: "When the entry was last modified.",
    usage: "Read-only. Surfaces recently edited entries during billing review.",
  },
};
